import * as v from 'valibot';

/**
 * Canonical control taxonomy. Each physical or graphical control on every
 * panel in the catalogue must classify as one of these types. The list is
 * exhaustive for Phase 1–5; surface any genuine gap to the user before
 * inventing a new type.
 *
 * Categories follow the bootstrap brief §9.
 */
export const ControlType = {
  // Rotary controls
  RotaryEncoderDetented: 'rotary-encoder-detented',
  RotaryEncoderContinuous: 'rotary-encoder-continuous',
  RotaryEncoderWithPush: 'rotary-encoder-with-push',
  RotaryEncoderWithIlluminationRing: 'rotary-encoder-with-illumination-ring',
  Potentiometer: 'potentiometer',
  KnobWithPointer: 'knob-with-pointer',
  KnobSkirted: 'knob-skirted',

  // Linear controls
  FaderLinear: 'fader-linear',
  FaderWithCentreDetent: 'fader-with-centre-detent',
  LeverTbar: 'lever-tbar',
  SlideSwitch: 'slide-switch',

  // Push controls
  PushMomentary: 'push-momentary',
  PushLatching: 'push-latching',
  PushExclusiveGroup: 'push-exclusive-group',
  PushIlluminated: 'push-illuminated',
  PushWithDisplay: 'push-with-display',
  KeySwitch: 'key-switch',

  // Mechanical switches
  ToggleSwitch: 'toggle-switch',
  RockerSwitch: 'rocker-switch',
  SelectorRotary: 'selector-rotary',

  // Joysticks and axis controls
  Joystick2Axis: 'joystick-2axis',
  Joystick2AxisWithPush: 'joystick-2axis-with-push',
  Joystick3Axis: 'joystick-3axis',
  Joystick3AxisWithPush: 'joystick-3axis-with-push',
  ShadingJoystick: 'shading-joystick',
  PtzJoystick: 'ptz-joystick',

  // Trackball
  Trackball: 'trackball',
  TrackballWithRing: 'trackball-with-ring',

  // Stacked rotary (concentric dual-ring rotaries — CGP-500 paint section)
  // Two concentric controls share one mechanical axis: the outer ring sets
  // one parameter (typically R/G/B Gain), the inner ring sets another
  // (typically R/G/B Black or Flare). The pair appears in groups of three
  // — one for each RGB channel — on colour-grading-class panels.
  StackedDualRingRotary: 'stacked-dual-ring-rotary',

  // Displays
  DisplaySegment: 'display-segment',
  DisplayDotMatrix: 'display-dot-matrix',
  DisplayLcdCharacter: 'display-lcd-character',
  DisplayLcdGraphic: 'display-lcd-graphic',
  DisplayOledMonochrome: 'display-oled-monochrome',
  DisplayOledColour: 'display-oled-colour',
  DisplayLedBargraph: 'display-led-bargraph',

  // Indicators
  LedDiscrete: 'led-discrete',
  LedRgb: 'led-rgb',
  LampIncandescent: 'lamp-incandescent',
  MeterVu: 'meter-vu',
  MeterLedBargraph: 'meter-led-bargraph',

  // Special
  JogShuttle: 'jog-shuttle',
  ChassisFeature: 'chassis-feature',
} as const satisfies Record<string, string>;

export type ControlType = (typeof ControlType)[keyof typeof ControlType];

export const ControlTypeSchema = v.picklist(Object.values(ControlType));
